/**
 * batch-71 gesprek-secties (FO-537) - de pure regels van de gesprekscontrole.
 *
 * Geen database: wat een afwijking is, wat een lijst tegenhoudt bij het afronden en hoe
 * een onderhoudsmoment wordt beoordeeld. De API en de kaarten in de voorbereiding delen
 * deze regels, zodat het scherm dezelfde melding geeft als de server.
 */

import {
  CONTROLE_KLACHT_GEEN,
  CONTROLE_ONDERHOUD_BIJNA_DAGEN,
  CONTROLE_ONDERHOUD_BIJNA_KM,
  CONTROLE_SCORE_AFWIJKING_TOT,
  type ControleAfwijkingDTO,
  type ControleBronSleutel,
  type ControleHouder,
  type ControleInhoudStatus,
  type ControleSignaal,
  type GesprekControleMiddel,
} from '@streep/types'
import { gvIsAfwijkendAntwoord } from './gespreksvoorbereiding'

export interface ControleAntwoordRegel {
  itemId: string
  vraag: string
  type: string
  verplicht: boolean
  risicoBijNee: boolean
  bronSleutel: ControleBronSleutel | string | null
  antwoordBool: boolean | null
  antwoordNum: number | null
  antwoordKeuze: string | null
  antwoordTekst: string | null
  heeftFoto: boolean
  /** Bij "compleet volgens lijst": de status per kofferregel, met de naam van de regel. */
  inhoud: Array<{ inhoudRegelId: string; omschrijving: string; status: ControleInhoudStatus; opmerking?: string | null }> | null
}

/** Wie een afwijking oppakt: een voertuig bij Wagenpark, de rest bij het Magazijn. */
export function controleHouderVoorMiddel(middel: GesprekControleMiddel): ControleHouder {
  return middel === 'VOERTUIG' ? 'WAGENPARK' : 'MAGAZIJN'
}

/** Een onderdeel dat niet aanwezig is (n.v.t. telt niet als afwijking). */
function afwijkendeRegels(regel: ControleAntwoordRegel) {
  return (regel.inhoud ?? []).filter((r) => r.status === 'ONTBREEKT' || r.status === 'KAPOT')
}

/**
 * De afwijkingen van een ingevulde lijst. "Een afwijking is nooit stil" (FO-537 §3.A4):
 * elke nee op een risicovraag, elke score van 2 of lager en elk ontbrekend of kapot
 * onderdeel. De opmerkingen uit het gesprek worden een afspraak: wel een actiepunt op het
 * verslag, geen melding. De sleutel is stabiel, zodat de actiepunten idempotent zijn.
 */
export function bepaalControleAfwijkingen(middel: GesprekControleMiddel, regels: ControleAntwoordRegel[]): ControleAfwijkingDTO[] {
  const houder = controleHouderVoorMiddel(middel)
  const uit: ControleAfwijkingDTO[] = []
  for (const r of regels) {
    if (r.bronSleutel === 'algemeen.opmerkingen') {
      const tekst = r.antwoordTekst?.trim()
      if (tekst) uit.push({ sleutel: r.itemId, omschrijving: tekst, soort: 'AFSPRAAK', houder: 'MEDEWERKER' })
      continue
    }
    if (r.bronSleutel === 'gereedschap.compleet') {
      const mis = afwijkendeRegels(r)
      if (mis.length > 0) {
        const namen = mis.map((m) => `${m.omschrijving} (${m.status === 'KAPOT' ? 'kapot' : 'ontbreekt'})`)
        uit.push({ sleutel: r.itemId, omschrijving: `${r.vraag}: ${namen.join(', ')}`, soort: 'AFWIJKING', houder })
        continue
      }
    }
    if (r.bronSleutel === 'voertuig.klachten') {
      const keuze = r.antwoordKeuze?.trim()
      if (keuze && keuze !== CONTROLE_KLACHT_GEEN) {
        const toelichting = r.antwoordTekst?.trim()
        uit.push({ sleutel: r.itemId, omschrijving: `Klacht: ${keuze}${toelichting ? ` - ${toelichting}` : ''}`, soort: 'AFWIJKING', houder })
      }
      continue
    }
    if (r.type === 'SCORE') {
      if (r.antwoordNum != null && r.antwoordNum <= CONTROLE_SCORE_AFWIJKING_TOT) {
        uit.push({ sleutel: r.itemId, omschrijving: `${r.vraag}: score ${r.antwoordNum}`, soort: 'AFWIJKING', houder })
      }
      continue
    }
    if ((r.type === 'JA_NEE' || r.type === 'JA_NEE_NVT') && r.risicoBijNee && r.antwoordBool === false) {
      const toelichting = r.antwoordTekst?.trim()
      uit.push({ sleutel: r.itemId, omschrijving: `${r.vraag}: nee${toelichting ? ` - ${toelichting}` : ''}`, soort: 'AFWIJKING', houder })
      continue
    }
    if (r.type === 'KEUZE' && gvIsAfwijkendAntwoord({ risicoBijNee: false, vraag: r.vraag, antwoordBool: null, antwoordKeuze: r.antwoordKeuze })) {
      uit.push({ sleutel: r.itemId, omschrijving: `${r.vraag}: ${r.antwoordKeuze}`, soort: 'AFWIJKING', houder })
    }
  }
  return uit
}

function heeftAntwoord(r: ControleAntwoordRegel): boolean {
  if (r.type === 'FOTO') return r.heeftFoto
  return (
    r.antwoordBool != null ||
    r.antwoordNum != null ||
    (r.antwoordKeuze != null && r.antwoordKeuze.trim() !== '') ||
    (r.antwoordTekst != null && r.antwoordTekst.trim() !== '') ||
    (r.inhoud != null && r.inhoud.length > 0)
  )
}

export interface ControleAfrondFout {
  itemId: string
  message: string
}

/**
 * Wat een lijst tegenhoudt bij het afronden. Inline per vraag: verplicht zonder antwoord,
 * foto ontbreekt bij een score van 2 of lager (spec 237 §6.6), een lagere km-stand zonder
 * reden (§6.2: wel op te slaan, niet zonder uitleg) en "niet compleet" zonder aan te geven
 * wat er ontbreekt. `laatsteKm` is de laatst bekende stand van het voertuig.
 */
export function valideerControleAfronden(regels: ControleAntwoordRegel[], context: { laatsteKm: number | null }): ControleAfrondFout[] {
  const fouten: ControleAfrondFout[] = []
  for (const r of regels) {
    if (r.type === 'HANDTEKENING') continue
    if (r.verplicht && !heeftAntwoord(r)) {
      fouten.push({ itemId: r.itemId, message: 'Vul deze vraag in' })
      continue
    }
    if (r.type === 'SCORE' && r.antwoordNum != null && r.antwoordNum <= CONTROLE_SCORE_AFWIJKING_TOT && !r.heeftFoto) {
      fouten.push({ itemId: r.itemId, message: 'Maak een foto: die is verplicht bij een score van 2 of lager' })
    }
    if (
      r.bronSleutel === 'voertuig.km' &&
      r.antwoordNum != null &&
      context.laatsteKm != null &&
      r.antwoordNum < context.laatsteKm &&
      !r.antwoordTekst?.trim()
    ) {
      fouten.push({ itemId: r.itemId, message: 'Deze stand is lager dan de laatste registratie: geef de reden' })
    }
    if (r.bronSleutel === 'gereedschap.compleet' && r.antwoordBool === false && afwijkendeRegels(r).length === 0) {
      fouten.push({ itemId: r.itemId, message: 'Geef per onderdeel aan wat er ontbreekt of kapot is' })
    }
  }
  return fouten
}

/** De laagste score op een lijst (voor de samenvatting en de trend), of null. */
export function laagsteControleScore(regels: Array<Pick<ControleAntwoordRegel, 'type' | 'antwoordNum'>>): number | null {
  const scores = regels.filter((r) => r.type === 'SCORE' && r.antwoordNum != null).map((r) => r.antwoordNum as number)
  return scores.length > 0 ? Math.min(...scores) : null
}

/**
 * Staat een onderhoudsmoment nog binnen het interval? TE_LAAT is voorbij datum of km;
 * BIJNA valt binnen de drempel; zonder drempels ONBEKEND (nooit stil "in orde").
 */
export function onderhoudSignaal(input: {
  vandaag: string
  vervalDatum: string | null
  vervalKm: number | null
  huidigeKm: number | null
}): ControleSignaal {
  const { vandaag, vervalDatum, vervalKm, huidigeKm } = input
  const heeftKm = vervalKm != null && huidigeKm != null
  if (!vervalDatum && !heeftKm) return 'ONBEKEND'
  if (vervalDatum && vervalDatum < vandaag) return 'TE_LAAT'
  if (heeftKm && huidigeKm >= vervalKm) return 'TE_LAAT'
  if (heeftKm && vervalKm - huidigeKm <= CONTROLE_ONDERHOUD_BIJNA_KM) return 'BIJNA'
  if (vervalDatum) {
    const dagen = Math.round((Date.parse(`${vervalDatum}T00:00:00Z`) - Date.parse(`${vandaag}T00:00:00Z`)) / 86_400_000)
    if (dagen <= CONTROLE_ONDERHOUD_BIJNA_DAGEN) return 'BIJNA'
  }
  return 'OK'
}
